/**
 * Mobile Incident Report Page
 * Allows creating incident reports offline with auto-sync
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { fetchAllPages } from '@/lib/api';
import SearchSelect from '@/components/common/SearchSelect';
import { participantLabel, PARTICIPANT_SEARCH_HINT } from '@/components/common/pickerLabels';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TrinitiDateTimePicker } from '@/components/triniti-datetime';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Save, AlertTriangle, Clock, MapPin, Camera, Users, ShieldQuestion, Info, Activity } from 'lucide-react';
import { queueOfflineAction, ACTION_TYPES } from '@/lib/offline/actionQueue';
import { syncAllActions, isOnline } from '@/lib/offline/syncEngine';
import { SyncStatusBadge, OfflineIndicator, SYNC_STATUS } from '@/components/mobile/SyncStatusBadge';
import MobileAttachmentCapture from '@/components/mobile/MobileAttachmentCapture';
import MobileAccessDenied from '@/components/mobile/MobileAccessDenied';
import { ATTACHMENT_ENTITY_TYPES } from '@/lib/offline/attachmentStorage';

const INCIDENT_TYPES = [
  { value: 'FALL', label: 'Fall' },
  { value: 'MEDICATION_ERROR', label: 'Medication Error' },
  { value: 'INJURY', label: 'Injury' },
  { value: 'BEHAVIORAL', label: 'Behavioral Incident' },
  { value: 'PROPERTY_DAMAGE', label: 'Property Damage' },
  { value: 'NEAR_MISS', label: 'Near Miss' },
  { value: 'ABUSE_ALLEGATION', label: 'Abuse Allegation' },
  { value: 'OTHER', label: 'Other' },
];

const CATEGORIES = [
  'Health/Medical', 'Behaviour', 'Environmental/Property', 'Medication',
  'Abuse/Neglect concern', 'Restrictive practice', 'Other',
];

const BODY_AREAS = [
  'Head', 'Face', 'Neck', 'Chest', 'Back', 'Abdomen',
  'Left arm', 'Right arm', 'Left hand', 'Right hand',
  'Left leg', 'Right leg', 'Left foot', 'Right foot',
];

const SEVERITY_LEVELS = [
  { value: 'MINOR', label: 'Minor', color: 'bg-green-500/10 text-green-600 border-green-500/30' },
  { value: 'MODERATE', label: 'Moderate', color: 'bg-amber-500/10 text-amber-600 border-amber-500/30' },
  { value: 'MAJOR', label: 'Major', color: 'bg-orange-500/10 text-orange-600 border-orange-500/30' },
  { value: 'CRITICAL', label: 'Critical', color: 'bg-red-500/10 text-red-600 border-red-500/30' },
];

export default function MobileIncidentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const participantId = searchParams.get('participantId');
  const shiftId = searchParams.get('shiftId');

  const [online, setOnline] = useState(isOnline());
  const [saving, setSaving] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [triageConfig, setTriageConfig] = useState(null);
  const [form, setForm] = useState({
    participantId: participantId || '',
    shiftId: shiftId || '',
    incidentType: '',
    severity: 'MINOR',
    description: '',
    location: '',
    immediateActions: '',
    witnesses: '',
    category: '',
    incidentDateTime: '',
    peopleInvolved: '',
    injuryIllnessFlag: false,
    emergencyServicesNotified: false,
    familyGuardianNotified: false,
    supervisorNotified: false,
    reportabilityTriage: {},
    bodyAreas: [],
    bodyMapNotes: '',
  });
  const [witnessList, setWitnessList] = useState([]);

  // Online/offline listener
  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Fetch participants
  useEffect(() => {
    if (online) {
      fetchAllPages('/participants')   // NEW-007: every assigned participant, not just the first 100
        .then(items => setParticipants(items || []))
        .catch(() => {});
    }
  }, [online]);

  // Day 27 — triage config
  useEffect(() => {
    api.get('/mobile/incident-triage-config')
      .then(res => setTriageConfig(res.data?.data || null))
      .catch(() => {});
  }, []);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const toggleTriage = (key) => {
    setForm(prev => ({ ...prev, reportabilityTriage: { ...prev.reportabilityTriage, [key]: !prev.reportabilityTriage[key] } }));
  };

  const toggleBodyArea = (area) => {
    setForm(prev => ({
      ...prev,
      bodyAreas: prev.bodyAreas.includes(area)
        ? prev.bodyAreas.filter(a => a !== area)
        : [...prev.bodyAreas, area],
    }));
  };

  const addWitness = () => setWitnessList(prev => [...prev, { name: '', contact: '', relationship: '', statement: '' }]);
  const updateWitness = (i, f, v) => setWitnessList(prev => prev.map((w, idx) => (idx === i ? { ...w, [f]: v } : w)));
  const removeWitness = (i) => setWitnessList(prev => prev.filter((_, idx) => idx !== i));

  const actionRequired = ['MODERATE', 'MAJOR', 'CRITICAL'].includes(form.severity);

  const saveDraft = async () => {
    if (!form.incidentType) {
      toast.error('Please select incident type');
      return;
    }
    if (!form.description.trim()) {
      toast.error('Please enter incident description');
      return;
    }
    if (actionRequired && !form.immediateActions.trim()) {
      toast.error('Immediate action taken is required for Moderate or higher severity');
      return;
    }

    setSaving(true);
    setSyncStatus(SYNC_STATUS.PENDING);

    try {
      // Queue the action for offline sync
      await queueOfflineAction(ACTION_TYPES.INCIDENT_REPORT, {
        participantId: form.participantId,
        shiftId: form.shiftId,
        incidentType: form.incidentType,
        severity: form.severity,
        description: form.description,
        location: form.location,
        immediateActions: form.immediateActions,
        witnesses: form.witnesses ? form.witnesses.split(',').map(w => w.trim()).filter(Boolean) : [],
        timestamp: new Date().toISOString(),
        attachmentIds: attachments.map(a => a.id),
        // Day 26
        category: form.category || null,
        incidentDateTime: form.incidentDateTime || null,
        peopleInvolved: form.peopleInvolved ? form.peopleInvolved.split(',').map(s => s.trim()).filter(Boolean) : [],
        injuryIllnessFlag: form.injuryIllnessFlag,
        emergencyServicesNotified: form.emergencyServicesNotified,
        familyGuardianNotified: form.familyGuardianNotified,
        supervisorNotified: form.supervisorNotified,
        // Day 27
        reportabilityTriage: form.reportabilityTriage,
        // Day 28
        witnessDetails: witnessList.filter(w => (w.name || '').trim()),
        bodyAreas: form.bodyAreas,
        bodyMapNotes: form.bodyMapNotes || null,
      });

      toast.success('Incident report saved locally', {
        description: online ? 'Syncing...' : 'Will sync when online',
      });

      // If online, try to sync immediately
      if (online) {
        setSyncStatus(SYNC_STATUS.SYNCING);
        try {
          const results = await syncAllActions();
          if (results.forbidden > 0) {
            setSyncStatus(SYNC_STATUS.FAILED);
            setAccessDenied(true);
          } else if (results.synced > 0) {
            setSyncStatus(SYNC_STATUS.SYNCED);
            toast.success('Report synced successfully');
            setTimeout(() => navigate(-1), 1000);
          } else if (results.failed > 0) {
            setSyncStatus(SYNC_STATUS.FAILED);
          }
        } catch (err) {
          setSyncStatus(SYNC_STATUS.FAILED);
        }
      } else {
        setTimeout(() => navigate(-1), 1500);
      }
    } catch (err) {
      toast.error('Failed to save report');
      setSyncStatus(SYNC_STATUS.FAILED);
    } finally {
      setSaving(false);
    }
  };

  const handleRetrySync = async () => {
    setSyncStatus(SYNC_STATUS.SYNCING);
    try {
      const results = await syncAllActions();
      if (results.synced > 0) {
        setSyncStatus(SYNC_STATUS.SYNCED);
        toast.success('Synced successfully');
      } else {
        setSyncStatus(SYNC_STATUS.FAILED);
      }
    } catch (err) {
      setSyncStatus(SYNC_STATUS.FAILED);
      toast.error('Sync failed');
    }
  };

  return (
    <>
    {accessDenied && <MobileAccessDenied />}
    <div className={`min-h-screen bg-background pb-24 ${accessDenied ? 'hidden' : ''}`} data-testid="mobile-incident-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-red-500 text-white px-4 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="text-white hover:bg-red-600" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              <h1 className="text-lg font-bold">Incident Report</h1>
            </div>
            <p className="text-xs text-red-100">Report safety incidents immediately</p>
          </div>
        </div>
      </div>

      {/* Offline/Sync Status */}
      <div className="px-4 pt-4 space-y-2">
        <OfflineIndicator isOffline={!online} />
        {syncStatus && (
          <SyncStatusBadge 
            status={syncStatus} 
            onRetry={syncStatus === SYNC_STATUS.FAILED ? handleRetrySync : undefined}
          />
        )}
      </div>

      {/* Form */}
      <div className="p-4 space-y-4">
        {/* Incident Type & Severity */}
        <Card className="border-red-500/20">
          <CardContent className="p-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="incidentType">Incident Type *</Label>
              <Select 
                value={form.incidentType || undefined} 
                onValueChange={(v) => handleChange('incidentType', v)}
              >
                <SelectTrigger id="incidentType" data-testid="incident-type-select">
                  <SelectValue placeholder="Select incident type" />
                </SelectTrigger>
                <SelectContent>
                  {INCIDENT_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Severity Level *</Label>
              <div className="grid grid-cols-2 gap-2">
                {SEVERITY_LEVELS.map(({ value, label, color }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => handleChange('severity', value)}
                    className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                      form.severity === value 
                        ? `${color} border-current` 
                        : 'bg-muted/50 border-transparent hover:bg-muted'
                    }`}
                    data-testid={`severity-${value.toLowerCase()}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Participant */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="participant">Participant Involved (Optional)</Label>
              <SearchSelect
                options={participants.map(p => ({ value: p.id, label: participantLabel(p), keywords: p.ndis_number || '' }))}
                value={form.participantId}
                onChange={(v) => handleChange('participantId', v)}
                placeholder="Select participant"
                searchPlaceholder={PARTICIPANT_SEARCH_HINT}
                emptyText="No participants match that search"
                clearLabel="None / General"
                clearValue="none"
                className="h-11"
                testId="participant-select"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="location"
                  placeholder="Where did this occur?"
                  value={form.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  className="pl-10"
                  data-testid="location-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Day 26 — incident details */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={form.category || undefined} onValueChange={(v) => handleChange('category', v)}>
                <SelectTrigger data-testid="category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="dt">Date &amp; time of incident</Label>
              <TrinitiDateTimePicker value={form.incidentDateTime} onChange={v => handleChange('incidentDateTime', v)} testId="incident-datetime" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="people">People involved</Label>
              <Input id="people" placeholder="Names, separated by commas"
                value={form.peopleInvolved} onChange={(e) => handleChange('peopleInvolved', e.target.value)}
                data-testid="people-involved-input" />
            </div>
          </CardContent>
        </Card>

        {/* Day 26 — injury flag + notifications */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2"><Activity className="h-4 w-4" /> Injury &amp; notifications</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={form.injuryIllnessFlag} onCheckedChange={(v) => handleChange('injuryIllnessFlag', !!v)} data-testid="injury-flag" />
              <span className="text-sm">Injury or illness occurred</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={form.emergencyServicesNotified} onCheckedChange={(v) => handleChange('emergencyServicesNotified', !!v)} data-testid="emergency-notified" />
              <span className="text-sm">Emergency services notified (000)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={form.familyGuardianNotified} onCheckedChange={(v) => handleChange('familyGuardianNotified', !!v)} data-testid="family-notified" />
              <span className="text-sm">Family / guardian notified</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={form.supervisorNotified} onCheckedChange={(v) => handleChange('supervisorNotified', !!v)} data-testid="supervisor-notified" />
              <span className="text-sm">Supervisor / on-call notified</span>
            </label>
          </CardContent>
        </Card>

        {/* Description */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">What Happened? *</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <Textarea
              id="description"
              placeholder="Describe the incident in detail..."
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={5}
              className="resize-none"
              data-testid="description-input"
            />
          </CardContent>
        </Card>

        {/* Immediate Actions */}
        <Card className={actionRequired ? 'border-amber-400' : ''}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Immediate Actions Taken{actionRequired ? ' *' : ''}</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <Textarea
              id="immediateActions"
              placeholder="What actions were taken immediately after the incident?"
              value={form.immediateActions}
              onChange={(e) => handleChange('immediateActions', e.target.value)}
              rows={3}
              className="resize-none"
              data-testid="actions-input"
            />
            {actionRequired && (
              <p className="text-xs text-amber-600 mt-1">Required for Moderate or higher severity.</p>
            )}
          </CardContent>
        </Card>

        {/* Day 27 — NDIS reportability triage */}
        <Card className="border-purple-300" data-testid="triage-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2"><ShieldQuestion className="h-4 w-4 text-purple-600" /> Reportability check</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-3">
            <div className="flex items-start gap-2 rounded-md bg-purple-500/10 p-2 text-xs text-purple-800">
              <Info className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{triageConfig?.notice || 'These questions help your coordinator review whether this may be reportable. This app does NOT submit anything to the NDIS.'}</span>
            </div>
            {(triageConfig?.reportableCategories || []).map(cat => (
              <label key={cat.key} className="flex items-start gap-2 cursor-pointer" data-testid={`triage-${cat.key}`}>
                <Checkbox checked={!!form.reportabilityTriage[cat.key]} onCheckedChange={() => toggleTriage(cat.key)} />
                <span className="text-sm">{cat.label}</span>
              </label>
            ))}
          </CardContent>
        </Card>

        {/* Day 28 — Witness details */}
        <Card data-testid="witness-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center justify-between">
              <span className="flex items-center gap-2"><Users className="h-4 w-4" /> Witnesses</span>
              <Button type="button" variant="outline" size="sm" onClick={addWitness} data-testid="add-witness-btn">Add witness</Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-3">
            {witnessList.length === 0 && <p className="text-xs text-muted-foreground">No witnesses added.</p>}
            {witnessList.map((w, i) => (
              <div key={i} className="border rounded-md p-3 space-y-2" data-testid={`witness-row-${i}`}>
                <div className="flex gap-2">
                  <Input placeholder="Name" value={w.name} onChange={(e) => updateWitness(i, 'name', e.target.value)} data-testid={`witness-name-${i}`} />
                  <Input placeholder="Contact" value={w.contact} onChange={(e) => updateWitness(i, 'contact', e.target.value)} />
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeWitness(i)} data-testid={`remove-witness-${i}`}>Remove</Button>
                </div>
                <Textarea placeholder="Statement" rows={2} className="resize-none" value={w.statement} onChange={(e) => updateWitness(i, 'statement', e.target.value)} />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Day 28 — Body area selector */}
        <Card data-testid="body-map-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Injury body areas</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-3">
            <div className="flex flex-wrap gap-2">
              {BODY_AREAS.map(area => (
                <button key={area} type="button" onClick={() => toggleBodyArea(area)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    form.bodyAreas.includes(area) ? 'bg-red-500 text-white' : 'bg-muted hover:bg-muted/80'
                  }`}
                  data-testid={`body-area-${area.toLowerCase().replace(/\s+/g, '-')}`}>
                  {area}
                </button>
              ))}
            </div>
            <Textarea placeholder="Notes on injuries (optional)" rows={2} className="resize-none"
              value={form.bodyMapNotes} onChange={(e) => handleChange('bodyMapNotes', e.target.value)} data-testid="body-map-notes" />
          </CardContent>
        </Card>

        {/* Attachments */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Camera className="h-4 w-4" />
              Incident Photos
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <MobileAttachmentCapture
              entityType={ATTACHMENT_ENTITY_TYPES.INCIDENT}
              entityId={null}
              maxAttachments={5}
              acceptedTypes="image/*"
              onAttachmentsChange={setAttachments}
              initialAttachments={attachments}
            />
          </CardContent>
        </Card>
      </div>

      {/* Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-background border-t p-4 safe-area-inset-bottom">
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            className="flex-1"
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button 
            className="flex-1 bg-red-500 hover:bg-red-600"
            onClick={saveDraft}
            disabled={saving || !form.incidentType || !form.description.trim()}
            data-testid="save-incident-btn"
          >
            {saving ? (
              <>
                <Clock className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <AlertTriangle className="h-4 w-4 mr-2" />
                {online ? 'Submit Report' : 'Save Draft'}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
    </>
  );
}
